import React, { useState, useEffect, useMemo } from "react";
import { Button, Field, Input, Message, Header, Table, useFrontier, Icon, Modal, Dropdown, Checkbox } from 'react-frontier2';
import { Link, LinkProps } from "react-router-dom";
import { EventDate, SetLoading } from "@arema/components/Classes";
import { EventSectionProps } from '../../AdminClasses';
import { bindFormChange, fromUnix, getNowLocal } from "@arema/components/Util";
import { useUser } from "../../AdminHooks";
import { firstBy } from "thenby";
import API from "../../API";
import Validator from "@arema/components/Validator";
import UserAccess from "../../UserAccess";
import { DateTime } from "luxon";
import { Trans, useTranslation } from "react-i18next";

interface DateDuplicate{
	date_id: number,
	origin: number,
	start_date: Date,
	start_now: boolean,
	amount: number,
	days_add: string,
	hours_add: string,
	minutes_add: string,
}

var EventContainer = (props: EventSectionProps)=>{
	var { t } = useTranslation();
	var { Toast } = useFrontier();
	var { user, hasAccess } = useUser();
	var [dates, setDates] = useState<EventDate[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [duplicateData, setDuplicateData] = useState<DateDuplicate>(null);
	var [duplicatePrompts, setDuplicatePrompts] = useState<string[]>(null);

	var loadDates = ()=>{
		API.getEventDates(props.event.event_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			setDates(res.data);
		}).catch(err=>{
			setLoadError(t('common.unexpected_code', { code: 'LCL-EVDTLST-1' }));
		});
	}

	useEffect(()=>{
		if(!dates){
			loadDates();
		}
	}, []);

	var DateList : React.ReactElement[] = useMemo(()=>{
		if(!dates) return [];
		var now = getNowLocal().toSeconds();
		var sorted_dates = dates.sort(firstBy((a: EventDate, b: EventDate)=>{
			return (a.date>=now ? 0 : 1) - (b.date>=now ? 0 : 1)
		}).thenBy((a: EventDate, b: EventDate)=>{
			return a.date>=now ? (a.date-b.date) : (b.date-a.date);
		}));

		var items : React.ReactElement[] = [];
		var is_past = false;
		for(var a of sorted_dates){
			if(a.date<=now && !is_past){
				is_past = true;
				items.push((
					<Table.Row header selectable={false} key={`PSTHDR`}>
						<Table.Cell colSpan={100}>
							{t('date_list.past_dates')}
						</Table.Cell>
					</Table.Row>
				))
			}
			items.push((
				<Table.Row compact key={`dte-${a.date_id}`}>
					<Table.Cell collapsing as={Link} to={`/events/${a.event_id}/dates/${a.date_id}`}>{a.date_id}</Table.Cell>
					<Table.Cell collapsing as={Link} to={`/events/${a.event_id}/dates/${a.date_id}`}>{fromUnix(a.date).toFormat('dd/MMM/yy HH:mm')}</Table.Cell>
					<Table.Cell as={Link} to={`/events/${a.event_id}/dates/${a.date_id}`}>{a.venue_name}</Table.Cell>
					<Table.Cell collapsing as={Link} to={`/events/${a.event_id}/dates/${a.date_id}`}>{a.city}</Table.Cell>
					<Table.Cell collapsing as={Link} to={`/events/${a.event_id}/dates/${a.date_id}`}>
						<Icon name={`eye${a.published ? '' : '-slash'}`} style={{ marginRight: 0 }} />
					</Table.Cell>
					<Table.Cell collapsing as={Link} to={`/events/${a.event_id}/dates/${a.date_id}`}>
						<Icon name={a.available_web ? 'check' : 'remove'} color={a.available_web ? 'green' : 'black'} style={{ marginRight: 0 }} />
					</Table.Cell>
					<Table.Cell collapsing as={Link} to={`/events/${a.event_id}/dates/${a.date_id}`}>
						<Icon name={a.available_pdv ? 'check' : 'remove'} color={a.available_pdv ? 'green' : 'black'} style={{ marginRight: 0 }} />
					</Table.Cell>
				</Table.Row>
			))
		}
		return items;
	}, [dates]);

	if(loadError) return <Header text={t('common.error')} subtext={loadError} />
	if(!dates) return <Header loading text={t('common.loading')} />

	var showDuplicate = ()=>{
		setDuplicateData({
			date_id: null,
			origin: 1,
			start_date: null,
			start_now: true,
			amount: 1,
			days_add: '0',
			hours_add: '0',
			minutes_add: '0'
		});
		setDuplicatePrompts(null);
	}

	var duplicateDate = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(duplicateData, {
			date_id: [
				{ rule: 'number', prompt: t('date_list.multiply.prompts.date_id') },
			],
			// select_start: ['empty', 'number'],
			start_date: [{
				prompt: t('date_list.multiply.prompts.start_date'),
				rule: ()=>{
					if(duplicateData.origin!=2) return true;
					return duplicateData.start_date && DateTime.fromJSDate(duplicateData.start_date).isValid;
				}
			}],
			// publish: ['empty'],
			amount: ['number', 'empty'],
			days_add: ['number'],
			hours_add: ['number'],
			minutes_add: ['number'],
		});
		setDuplicatePrompts(prompts);
		if(!valid) return;
		setLoading(true);
		API.duplicateDate(duplicateData.date_id, duplicateData.origin, duplicateData.start_date ? Math.floor(duplicateData.start_date.getTime()/1000) : null, duplicateData.amount, duplicateData.start_now, {
			days: parseInt(duplicateData.days_add),
			hours: parseInt(duplicateData.hours_add),
			minutes: parseInt(duplicateData.minutes_add),
		}).then(res=>{
			if(res.error) return setDuplicatePrompts([res.message]);
			Toast.success(t('date_list.date_multiplied'));
			setDuplicateData(null);
			setDates(null);
			loadDates();
		}).catch(err=>{
			setDuplicatePrompts([t('common.unexpected_code', { code: 'LCL-EVDTL-MLT-1' })]);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var getDuplicateStart = ()=>{
		if(!duplicateData || !duplicateData.date_id) return 'ERROR';
		var start_date = null;
		if(duplicateData.origin==2){
			start_date = duplicateData.start_date;
		}else{
			var date = dates.find(a=>a.date_id==duplicateData.date_id);
			if(!date) return 'ERROR';
			start_date = fromUnix(date.date).toJSDate();
		}
		return DateTime.fromJSDate(start_date).plus({
			day: Number.isNaN(parseInt(duplicateData.days_add)) ? 0 : parseInt(duplicateData.days_add),
			hour: Number.isNaN(parseInt(duplicateData.hours_add)) ? 0 : parseInt(duplicateData.hours_add),
			minute: Number.isNaN(parseInt(duplicateData.minutes_add)) ? 0 : parseInt(duplicateData.minutes_add),
		}).toFormat('dd/MMM/yy HH:mm');
	}

	var onDuplicateFormChange = bindFormChange(duplicateData, setDuplicateData);

	return <div>
		<Table selectable style={{ maxWidth: 800, margin: 'auto' }}>
			<Table.Head
				title={t('date_list.title')}
				titleRight={<div>
					{hasAccess(UserAccess.EVENTS.CREATE_EVENT) && <Button size='tiny' text={t('date_list.multiply.button')} iconName='copy' style={{ marginRight: 5 }} onClick={showDuplicate} />}
					<Button size='tiny' text={t('common.create')} iconName='add' color='black' as={Link} to={'dates/create'} />
				</div>}
				centeredIndexes={[4, 5, 6]}
				collapsingIndexes={[0, 1, 3, 4, 5, 6]}
				data={[[
					...(t('date_list.table_headers', { returnObjects: true }) as any),
					<Icon name="eye" style={{ marginRight: 0 }} />,
					<Icon name="globe" style={{ marginRight: 0 }} />,
					<Icon name="cash-register" style={{ marginRight: 0 }} />,
				]]}
			/>
			<Table.Body>
				{dates.length===0 && (
					<Table.Cell empty row colSpan={100} value={t('date_list.empty_dates')} />
				)}
				{DateList}
			</Table.Body>
		</Table>
		<Modal open={!!duplicateData} onClose={()=>setDuplicateData(null)} size="tiny">
			<Modal.Header>{t('date_list.multiply.modal.title')}</Modal.Header>
			{duplicateData && <Modal.Content>
				<Dropdown 
					label={t('date_list.multiply.modal.base_date_label')} 
					placeholder={t('date_list.multiply.modal.base_date_comment')}
					value={duplicateData.date_id} 
					onValueChange={onDuplicateFormChange('date_id')} 
					items={dates.map(a=>({
						value: a.date_id,
						text: fromUnix(a.date).toFormat('dd/MMM/yy HH:mm'),
						meta: `ID: ${a.date_id}`
					}))} 
				/>
				<Input label={t('date_list.multiply.modal.amount')} value={duplicateData.amount} onChange={onDuplicateFormChange('amount')} />
				<Header text={t('date_list.multiply.modal.changes_title')} size="small" style={{ marginTop: 15, marginBottom: 5 }} />
				<Dropdown 
					label={t('date_list.multiply.modal.from_label')}
					placeholder={t('date_list.multiply.modal.from_comment')}
					value={duplicateData.origin}
					onValueChange={v=>{
						var dd = {...duplicateData};
						if(v===2 && dd.date_id){
							var date = dates.find(a=>a.date_id==dd.date_id);
							if(date){
								dd.start_date = fromUnix(date.date).toJSDate();
							}
						}else{
							dd.start_date = null;
						}
						return onDuplicateFormChange('origin')(v);
					}}
					items={[
						{ value: 1, text: t('date_list.multiply.modal.from_options.date_date') },
						{ value: 2, text: t('date_list.multiply.modal.from_options.select_date') },
					]} 
				/>
				{duplicateData.origin==2 ? (
					<Input label={t('date_list.multiply.modal.select_date_label')} placeholder={t('date_list.multiply.modal.select_date_comment')} calendar={{ 
						date: duplicateData.start_date, 
						mode: 'date',
						onSelected: onDuplicateFormChange('start_date'),
					}} />
				) : null}
				<Field amount={3}>
					<Input label={t('date_list.multiply.modal.change_options.days')} value={duplicateData.days_add} onChange={onDuplicateFormChange('days_add')} />
					<Input label={t('date_list.multiply.modal.change_options.hours')} value={duplicateData.hours_add} onChange={onDuplicateFormChange('hours_add')} />
					<Input label={t('date_list.multiply.modal.change_options.minutes')} value={duplicateData.minutes_add} onChange={onDuplicateFormChange('minutes_add')} />
				</Field>
				<Field label={t('date_list.multiply.modal.start_now_label')} comment={t('date_list.multiply.modal.start_now_comment')}>
					<Checkbox label={t('date_list.multiply.modal.start_now_label')} checked={duplicateData.start_now} onChange={onDuplicateFormChange('start_now')} style={{ marginTop: 5 }} />
				</Field>
				{duplicateData.date_id && duplicateData.amount ? (
					<Message type="info">
						<Trans i18nKey={'date_list.multiply.modal.duplicate_info'} values={{ count: duplicateData.amount, start: getDuplicateStart() }} />
					</Message>
				) : null}
				<Message type="error" list={duplicatePrompts} style={{ marginTop: 10 }} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text={t('date_list.multiply.button')} color="black" onClick={duplicateDate} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default EventContainer;